import useAdminAccess from "hooks/useAdminAccess";
import { requiredAdminPermissionsForPath } from "services/adminAccess";
import AdminSidebar from "components/layout/AdminSidebar";
import Main from "components/layout/Main";
import { ReactNode } from "react";
import { Navigate, useParams } from "react-router-dom";
import Link from "components/admin/AdminLink";
import Fulfilment from "./Fulfilment";
import History from "./History";
import Overview from "./Overview";
import Progression from "./Progression";
import Rewards from "./Rewards";
import Streaks from "./Streaks";
import EloActions from "./EloActions";

const sections = [
  { key: "overview", label: "Overview", icon: "fa-house" },
  { key: "progression", label: "ELO & tiers", icon: "fa-layer-group" },
  { key: "actions", label: "Actions", icon: "fa-bolt" },
  { key: "streaks", label: "Streak & milestones", icon: "fa-fire" },
  { key: "rewards", label: "Rewards", icon: "fa-gift" },
  { key: "fulfilment", label: "Pending rewards", icon: "fa-list-check" },
] as const;

const views: Record<string, ReactNode> = {
  overview: <Overview />,
  progression: <Progression />,
  actions: <EloActions />,
  streaks: <Streaks />,
  rewards: <Rewards />,
  fulfilment: <Fulfilment />,
  history: <History />,
};

export default function GamificationAdmin() {
  const { canAny } = useAdminAccess();
  const visibleSections = sections.filter((item) => canAny(requiredAdminPermissionsForPath(`/gamification/${item.key}`) ?? []));
  const { section = "overview" } = useParams();
  if (!views[section]) {
    return <Navigate replace to="/gamification/overview" />;
  }

  return (
    <div className="min-h-screen bg-[#f7f7f5] md:flex">
      <div className="hidden md:contents">
        <AdminSidebar />
      </div>
      <div className="sticky top-0 z-30 flex items-center justify-between bg-black px-5 py-4 md:hidden">
        <Link to="/">
          <img
            className="w-[118px]"
            alt="Bitoshi.africa logo"
            src="/images/logo-white.png"
          />
        </Link>
        <span className="rounded-lg border border-gray-700 px-3 py-2 text-xs font-semibold text-gray-200">
          Admin · Gamification
        </span>
      </div>
      <Main className="w-full pb-12 md:w-5/6">
        <div className="mx-auto max-w-[1500px]">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight text-gray-950">
                Gamification
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-500">
                Manage how users earn ELO points, build streaks and
                receive rewards.
              </p>
            </div>
            <Link
              className="text-sm font-semibold text-gray-600 hover:text-gray-950 focus:outline-none focus:ring-2 focus:ring-orange-400"
              to="/gamification/history"
            >
              <i className="fa fa-clock-rotate-left mr-2" />
              Change history
            </Link>
          </header>

          <nav
            className="mt-7 flex gap-1 overflow-x-auto rounded-xl border border-gray-200 bg-white p-1.5"
            aria-label="Gamification sections"
          >
            {visibleSections.map((item) => {
              const active = item.key === section;
              return (
                <Link
                  key={item.key}
                  to={`/gamification/${item.key}`}
                  className={`flex shrink-0 items-center gap-2 rounded-lg px-3.5 py-2.5 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                    active
                      ? "bg-gray-950 text-white"
                      : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  <i className={`fa ${item.icon} text-xs`} />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <main className="mt-6">
            {section === "history" && (
              <Link
                className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-950"
                to="/gamification/overview"
              >
                <i className="fa fa-arrow-left" />
                Back to gamification
              </Link>
            )}
            {views[section]}
          </main>
        </div>
      </Main>
    </div>
  );
}
